import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { BehaviorSubject, map, Observable } from "rxjs";
import { IAdminLoginVerifyRequest, IAdminService_UpdateRevenueSettingsRequest } from "./admin.model";
import { ApiService } from "../../api/api.service";
import { AccountService } from "../../account/account.service";
import { IUserLogin } from "../../account/account.model";
import { IUsersApiResponse, IWaitlistUserReq, IUserRoleUpdateReq, IUserRoleUpdateRes, IBanUserData, IUserDetailsResponse } from "../../../_models/users.model";
import { environment } from "../../../../environments/environment";
import { IPagedRequest } from "../../../_models/request.model";
import { IAdminSettingsRes } from "../../../_models/settings.model";
import { SessionService } from "../../session/session.service";


@Injectable({
  providedIn: "root",
})
export class AdminService {

  public endpoint: string = "admin";

  constructor(
    private apiService: ApiService,
    private sessionService: SessionService,
    private accountService: AccountService
  ) { }
  public currentUserSource = new BehaviorSubject<IUserLogin | null>(
    this.getUserFromStorage()
  );
  public currentUser$ = this.currentUserSource.asObservable();

  private getUserFromStorage(): IUserLogin | null {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        return JSON.parse(userString);
      }
    } catch (error) {
      console.error("Error parsing user from localStorage:", error);
      localStorage.removeItem("user");
    }
    return null;
  }

  joinWaitlist(body: IWaitlistUserReq) {
    const data: IWaitlistUserReq = {
      firstName: body.firstName,
      lastName: body.lastName,
      email: body.email,
      isOptionalFieldEnabled: body.isOptionalFieldEnabled
    }

    if (body.isOptionalFieldEnabled) {
      data.phone = body.phone;
      data.preferredContactMethod = body.preferredContactMethod;
      data.leadSource = body.leadSource;
      data.specificQuestionsOrRequests = body.specificQuestionsOrRequests;
    }

    return this.apiService.post<IWaitlistUserReq, object>({
      Url: `external/join-app-waitlist/appId/${environment.DropAddyAppId}`,
      Body: data,
      BaseUrl: environment.insizonBackendBaseUrl
    });
  }

  getJoinWaitlist() {
    return this.apiService.getAllPageed<IWaitlistUserReq>({
      Url: `external/join-app-waitlist/appId/${environment.DropAddyAppId}`,
      BaseUrl: environment.insizonBackendBaseUrl
    });
  }

  getUserById(id: string) {
    return this.apiService.get<any>({
      Url: `${this.endpoint}/users/${id}`
    });
  }

  getAllUsers(req: IPagedRequest) {
    let params = new HttpParams({
      fromObject: {
        page: String(req.page),
        limit: String(req.limit),
        ...(req.search && { search: req.search }),
        ...(req.sort && { sortBy: req.sort }),
        ...(req.sortOrder && { sortOrder: req.sortOrder }),
        ...(req.includeDeleted && { includeDeleted: 'true' }),
        ...(req.subscriptionType && { subscriptionType: req.subscriptionType }),
        ...(req.subscriptionStatus && { subscriptionStatus: req.subscriptionStatus })
      },
    });

    return this.apiService.get<IUsersApiResponse>({
      Url: `${this.endpoint}/users`,
      Params: params
    });
  }

  updateUserStatus(
    userId: string,
    isActive?: boolean,
    accountStatus?: string,
    suspendedUntil?: string,
    reason?: string,
    sendEmail?: boolean,
    emailSubject?: string,
    emailDescription?: string,
    suspendedServices?: string[]
  ) {
    return this.apiService.put<any, any>({
      Url: `${this.endpoint}/users/status`,
      Body: {
        userId: userId,
        isActive: isActive,
        accountStatus: accountStatus,
        suspendedUntil: suspendedUntil,
        suspendedServices: suspendedServices,
        reason: reason,
        sendEmail: sendEmail,
        emailSubject: emailSubject,
        emailDescription: emailDescription
      }
    });
  }

  updateUserRole(userIds: string[], role: 'user' | 'admin' | 'super_admin') {
    return this.apiService.put<IUserRoleUpdateReq, IUserRoleUpdateRes>({
      Url: `${this.endpoint}/users/role`,
      Body: {
        userIds: userIds,
        role: role,
      }
    });
  }

  deleteUsers(userIds: string[]) {
    return this.apiService.put<{ userIds: string[] }, any>({
      Url: `${this.endpoint}/users/delete`,
      Body: { userIds }
    });
  }

  getUserDetails(userId: string) {
    return this.apiService.get<IUserDetailsResponse>({
      Url: `${this.endpoint}/users/${userId}`
    });
  }

  banUser(userId: string) {
    return this.apiService.put<null, IBanUserData>({
      Url: `${this.endpoint}/users/${userId}/ban`,
      Body: null
    });
  }

  deleteLobbies(lobbyIds: string[]) {
    return this.apiService.put<{ lobbyIds: string[] }, any>({
      Url: `${this.endpoint}/lobbies/delete`,
      Body: { lobbyIds }
    });
  }

  getRevenueSettings() {
    return this.apiService.get<IAdminSettingsRes>({
      Url: `${this.endpoint}/settings`,
    });
  }

  updateRevenueSettings(props: IAdminService_UpdateRevenueSettingsRequest) {
    return this.apiService.put<IAdminService_UpdateRevenueSettingsRequest, IAdminSettingsRes>({
      Url: `${this.endpoint}/settings`,
      Body: props,
    });
  }

  verifyAdminLogin(body: IAdminLoginVerifyRequest, rememberMe: boolean) {
    return this.apiService.post<IAdminLoginVerifyRequest, any>({
      Url: `auth/admin-login-verify`,
      Body: body
    }).pipe(
      map(res => {
        if (res) {
          localStorage.setItem("user", JSON.stringify(res.data));
          this.currentUserSource.next({
            firstName: res.data.firstName,
            role: res.data.role,
            token: res.auth.token,
            uuidv4Id: res.data.uuidv4Id
          });
          if (res.auth && res.auth.token) {
            try {
              this.sessionService.setSession(
                res.auth.token,
                res.data,
                rememberMe
              );
            } catch (err) {
              console.warn("Unable to set session via SessionService:", err);
            }
          }
        }
        return res;
      })
    );
  }

  resendAdminLoginOtp(email: string) {
    return this.apiService.post<{ email: string }, any>({
      Url: `auth/resend-admin-login-otp`,
      Body: { email }
    });
  }
}
